import { createTheme, ThemeOptions } from '@mui/material';

import { Colors, SolidColors, TransparentColors } from './bolt-uikit-colors';
import { tailwindBreakpointOverrides } from './tailwindCompatibility';

export const FONT_FAMILY = 'euclidCircularB, sans-serif';

export const APP_BAR_HEIGHT_IN_PIXELS = 74;

export enum BorderRadius {
  StandardDialog = '16px',
  Input = '0.5rem',
}
export enum Shadows {
  StandardDialog = '0 0 20px 0 #2224',
}

/**
 * Because we expect fallback to `sans-serif` for some languages (Greek),
 * semibold (600) might not be available due to the operating system's default font.
 */
export enum FontWeight {
  Bold = 800,
  SemiBold = 600,
  Normal = 400,
}

const makeFontSizeAndLineHeight = (
  fontSize: number,
  lineHeight: number,
  fontWeight: FontWeight,
  color: string,
  letterSpacing: number | 'normal' = 'normal',
) => {
  return {
    color,
    fontWeight,
    fontStyle: 'normal',
    fontSize: `${fontSize / 16}rem`,
    lineHeight: `${lineHeight / 16}rem`,
    letterSpacing: letterSpacing === 'normal' ? 'normal' : `${letterSpacing / 16}rem`,
  };
};

// theme instance for menu
const customBreakpointValues = {
  values: {
    xs: 0,
    sm: 472,
    md: 720,
    // lg = xl - sidebar width
    lg: 950,
    xl: 1200,
  },
};

const themeDefaults: Partial<ThemeOptions> = {
  palette: {
    colors: Colors,
    primary: {
      main: Colors.green500,
      dark: Colors.green700,
      light: Colors.green300,
      contrastText: Colors.white,
    },
    secondary: {
      main: Colors.neutral200,
      dark: Colors.neutral300,
      light: Colors.neutral100,
      contrastText: Colors.neutral900,
    },
    warning: {
      main: Colors.yellow100,
      contrastText: Colors.neutral900,
    },
    danger: {
      main: Colors.red500,
      dark: Colors.red700,
      light: Colors.red300,
      contrastText: Colors.white,
    },
    text: {
      primary: Colors.neutral900,
      secondary: Colors.neutral700,
      disabled: Colors.neutral600,
    },
  },
  typography: {
    fontFamily: FONT_FAMILY,
    h1: makeFontSizeAndLineHeight(48, 56, FontWeight.SemiBold, Colors.neutral900),
    h2: makeFontSizeAndLineHeight(40, 48, FontWeight.SemiBold, Colors.neutral900),
    h3: makeFontSizeAndLineHeight(32, 40, FontWeight.SemiBold, Colors.neutral900),
    h4: makeFontSizeAndLineHeight(28, 36, FontWeight.SemiBold, Colors.neutral900),
    h5: makeFontSizeAndLineHeight(24, 30, FontWeight.SemiBold, Colors.neutral900),
    h6: makeFontSizeAndLineHeight(20, 26, FontWeight.SemiBold, Colors.neutral900),
    title: makeFontSizeAndLineHeight(24, 32, FontWeight.SemiBold, Colors.neutral900),
    subtitleL: makeFontSizeAndLineHeight(18, 28, FontWeight.SemiBold, Colors.neutral900),
    subtitleM: makeFontSizeAndLineHeight(16, 24, FontWeight.SemiBold, Colors.neutral900),
    subtitleS: makeFontSizeAndLineHeight(14, 20, FontWeight.SemiBold, Colors.neutral900),
    subtitleXS: makeFontSizeAndLineHeight(12, 16, FontWeight.SemiBold, Colors.neutral900),
    bodyL: makeFontSizeAndLineHeight(18, 28, FontWeight.Normal, Colors.neutral900),
    bodyM: makeFontSizeAndLineHeight(16, 24, FontWeight.Normal, Colors.neutral900),
    bodyS: makeFontSizeAndLineHeight(14, 20, FontWeight.Normal, Colors.neutral900),
    bodyXS: makeFontSizeAndLineHeight(12, 16, FontWeight.Normal, Colors.neutral900),
    caption: makeFontSizeAndLineHeight(12, 16, FontWeight.Normal, Colors.neutral900),
    ghostButtonTitle: {
      fontSize: '16px',
      lineHeight: '24px',
      fontWeight: 600,
      color: Colors.neutral100,
    },
    highlightGreen: {
      fontSize: '12px',
      lineHeight: '16px',
      fontWeight: 'normal',
      color: Colors.green500,
      backgroundColor: Colors.green50,
      padding: '2px 4px',
    },
    highlightRed: {
      fontSize: '12px',
      lineHeight: '16px',
      fontWeight: 'normal',
      color: Colors.red500,
      backgroundColor: Colors.red100,
      padding: '2px 4px',
    },
  },
  components: {
    MuiButtonBase: {
      defaultProps: {
        disableTouchRipple: true,
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          whiteSpace: 'nowrap',
          '&:hover': {
            boxShadow: 'none',
          },
          '&:active': {
            boxShadow: 'none',
            transform: 'scale(.975)',
            transition: 'all .1s ease-in-out',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '100px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
          '&:active': {
            boxShadow: 'none',
          },
        },
        sizeLarge: {
          minWidth: '106px',
          padding: '16px 24px 16px 24px',
          fontSize: '18px',
          fontWeight: 600,
          lineHeight: '24px',
        },
        sizeMedium: {
          minWidth: '100px',
          padding: '12px 24px 12px 24px',
          fontSize: '16px',
          fontWeight: 600,
          lineHeight: '24px',
        },
        sizeSmall: {
          minWidth: '92px',
          padding: '8px 20px 8px 20px',
          fontSize: '16px',
          fontWeight: 600,
          lineHeight: '24px',
        },
        sizeExtraSmall: {
          minWidth: '85px',
          padding: '6px 20px 6px 20px',
          fontSize: '14px',
          fontWeight: 600,
          lineHeight: '20px',
        },
        textPrimary: {
          color: Colors.green700,
        },
        textSecondary: {
          color: Colors.neutral700,
        },
        textDanger: {
          color: Colors.red700,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: Colors.neutral900,
          '&:hover': {
            backgroundColor: Colors.neutral300,
          },
          '&:active': {
            color: Colors.white,
            backgroundColor: Colors.transparent900,
          },
        },
        sizeLarge: {
          padding: '16px',
          '& .MuiSvgIcon-root': {
            fontSize: '24px',
            lineHeight: '24px',
          },
        },
        sizeMedium: {
          padding: '13px',
          '& .MuiSvgIcon-root': {
            fontSize: '22px',
            lineHeight: '22px',
          },
        },
        sizeSmall: {
          padding: '10px',
          '& .MuiSvgIcon-root': {
            fontSize: '20px',
            lineHeight: '20px',
          },
        },
        sizeExtraSmall: {
          padding: '7px',
          '& .MuiSvgIcon-root': {
            fontSize: '18px',
            lineHeight: '18px',
          },
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        colorPrimary: {
          color: `${Colors.green500}!important`,
        },
        colorSecondary: {
          color: `${Colors.neutral900}!important`,
        },
        colorPublished: {
          color: `${Colors.green500}!important`,
        },
        colorDraft: {
          color: `${Colors.yellow700}!important`,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px 24px 24px',
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: `${APP_BAR_HEIGHT_IN_PIXELS}px!important`,
          backgroundColor: Colors.white,
          boxShadow: `inset 0px -1px 0px ${Colors.neutral400}`,
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        colorDefault: {
          backgroundColor: Colors.neutral800,
          color: 'rgba(255, 255, 255, 0.48)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: 'none',
          boxShadow: 'none !important',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderRadius: '8px',
          '&.MuiCard-root': {
            borderRadius: '16px',
          },
        },
        elevation8: {
          boxShadow:
            '0px 60px 140px rgba(47, 49, 63, 0.04), 0px 3px 10px rgba(47, 49, 63, 0.13), 0px 18px 40px rgba(47, 49, 63, 0.06), 0px 7px 26px rgba(47, 49, 63, 0.08)!important',
          borderRadius: '8px!important',
        },
        elevation1: {
          boxShadow:
            '0px 60px 140px rgba(47, 49, 63, 0.04), 0px 3px 10px rgba(47, 49, 63, 0.13), 0px 18px 40px rgba(47, 49, 63, 0.06), 0px 7px 26px rgba(47, 49, 63, 0.08)!important',
          borderRadius: '8px!important',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          '&:last-child': {
            paddingBottom: '16px',
          },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '& a.active': {
            color: Colors.green700,
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: Colors.neutral900,
          fontSize: '16px',
          lineHeight: '24px',
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .MuiFormLabel-root': {
            fontSize: '16px',
            lineHeight: '24px',
            fontWeight: 'normal',
            color: Colors.neutral700,
          },
          '& .MuiFormLabel-root:focused': {
            color: Colors.neutral600,
          },
          '& .MuiInputLabel-filled': {
            transform: 'translate(16px, 16px) scale(1)',
          },
          '& .MuiInputLabel-filled.MuiInputLabel-shrink': {
            transform: 'translate(16px, 8px) scale(0.75)',
            color: Colors.neutral600,
          },
          '& .MuiInputBase-root': {
            fontSize: '16px',
            lineHeight: '24px',
            fontWeight: 'normal',
            color: Colors.neutral900,
          },
          '& .MuiInputBase-input': {
            height: '20px',
            padding: 0,
            '&:focus': {
              backgroundColor: 'initial',
            },
          },
          '& .MuiFilledInput-root': {
            paddingLeft: '16px',
            minHeight: '56px',
            backgroundColor: Colors.neutral200,
            borderRadius: '4px 4px 0 0',
            '&::before': { borderBottom: 'none', transition: 'none' },
            '&::after': { borderBottom: 'none' },
            '&:hover:not(.Mui-disabled):before': {
              borderBottom: 'none',
            },
            '&:hover': {
              borderBottom: 'none',
            },
            '&:hover:before': {
              borderBottom: 'none',
            },
            '& .MuiInputAdornment-root.MuiInputAdornment-positionStart': {
              marginTop: '20px!important',
            },
            '&.Mui-focused': {
              borderBottom: 'solid 2px',
              borderBottomColor: Colors.green700,
            },
          },
          '& .MuiInputBase-multiline': {
            paddingTop: '4px',
            paddingBottom: 0,
          },
          '& .MuiFilledInput-input': {
            marginTop: '20px',
          },
          '& .MuiOutlinedInput-root': {
            paddingLeft: '16px',
            paddingRight: '16px',
            minHeight: '56px',
            '& .MuiInputAdornment-root.MuiInputAdornment-positionStart': {
              marginTop: '0px!important',
            },
            '& input:-webkit-autofill': {
              WebkitBoxShadow: '0 0 0 1000px white inset',
            },
          },
          '& .MuiAutocomplete-root': {
            '& .MuiFilledInput-root': {
              paddingTop: 0,
            },
            '& .MuiFilledInput-input': {
              paddingLeft: '8px!important',
            },
            '& .Mui-error': {
              backgroundColor: Colors.red100,
              color: Colors.red700,
            },
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          fontSize: '16px',
          lineHeight: '24px',
          fontWeight: 'normal',
          alignItems: 'center',
          '& .MuiSvgIcon-root': {
            marginTop: 0,
          },
        },
        filledWarning: {
          color: Colors.neutral900,
        },
        icon: {
          padding: 0,
          fontSize: '20px',
          lineHeight: '24px',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        arrow: {
          color: Colors.neutral900,
        },
        tooltip: {
          backgroundColor: Colors.neutral900,
          color: Colors.white,
          fontSize: '14px',
          lineHeight: '20px',
          fontWeight: 'normal',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: Colors.green700,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        sizeSmall: {
          height: '28px',
          fontSize: '14px',
        },
        labelSmall: {
          padding: '0 12px',
        },
        sizeMedium: {
          height: '36px',
          fontSize: '16px',
        },
        labelMedium: {
          padding: '0 16px',
        },
      },
    },
  },
};
export const theme = createTheme({
  ...themeDefaults,
  breakpoints: customBreakpointValues,
});

export const themeWithTailwindBreakpointValues = createTheme({
  ...themeDefaults,
  breakpoints: tailwindBreakpointOverrides,
});

declare module '@mui/material/styles/createPalette' {
  interface PaletteOptions {
    danger?: PaletteColorOptions;
    colors?: { [key: string]: SolidColors | TransparentColors };
  }
}

declare module '@mui/material/styles/createTypography' {
  type CustomVariant =
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 'title'
    | 'subtitleL'
    | 'subtitleM'
    | 'subtitleS'
    | 'subtitleXS'
    | 'caption'
    | 'button'
    | 'overline'
    | 'bodyL'
    | 'bodyM'
    | 'bodyS'
    | 'bodyXS'
    | 'highlightGreen'
    | 'highlightRed'
    | 'ghostButtonTitle';

  interface Typography extends Record<CustomVariant, TypographyStyle>, FontStyle, TypographyUtils {}

  interface TypographyOptions
    extends Partial<Record<CustomVariant, TypographyStyleOptions> & FontStyleOptions> {}
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    subtitle1: false;
    subtitle2: false;
    subtitleL: true;
    subtitleM: true;
    subtitleS: true;
    subtitleXS: true;
    body1: false;
    body2: false;
    bodyL: true;
    bodyM: true;
    bodyS: true;
    bodyXS: true;
    ghostButtonTitle: true;
    highlightGreen: true;
    highlightRed: true;
    title: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsSizeOverrides {
    extraSmall: true;
  }
  interface ButtonPropsColorOverrides {
    danger: true;
  }
  interface ButtonPropsVariantOverrides {
    outlined: false;
  }
}

declare module '@mui/material/Button/buttonClasses' {
  interface ButtonClasses {
    sizeExtraSmall: string;
    textDanger: string;
  }
}

declare module '@mui/material/IconButton' {
  interface IconButtonPropsSizeOverrides {
    extraSmall: true;
  }
}

declare module '@mui/material/SvgIcon' {
  interface SvgIconClasses {
    colorPublished: string;
    colorDraft: string;
  }
  interface SvgIconPropsColorOverrides {
    published: true;
    draft: true;
  }
}

declare module '@mui/material/IconButton/iconButtonClasses' {
  interface IconButtonClasses {
    sizeExtraSmall: string;
  }
}
